import React, { useState, useRef, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import { Popover, Overlay, Row, Col, Button, Modal, Alert } from 'react-bootstrap';
import './Modal.css';
import './Dropdowns.css';
import DataDropdown from './DataDropdown';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from "react-oidc-context";
import { useUser } from "../context/UserContext";
import { userManagementAPI } from '../utils/apiClient';
import { trackUserManagement } from '../utils/analytics';

// Define demo account hash
const DEMO_ACCOUNT_HASH = '60ea09b922c70e13788e44b50ca0fa89b05edad1de460477504077f3f7cc408c';

function AddUsersModal(props) {
  const auth = useAuth();
  const { orgHash, isSuperadmin } = useUser();
  
  // Form state
  const [emailList, setEmailList] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [selectedPermission, setSelectedPermission] = useState("");
  
  // Dropdown data from API
  const [domains, setDomains] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
  // Submission loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Static permissions list
  const permissions = ["Restricted", "Full", "Owner"];
  
  // Popover state
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const [popoverType, setPopoverType] = useState("");
  const buttonRef = useRef(null);

  // Fetch dropdown data when modal opens
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!props.show) return;
      
      const token = auth.user?.access_token;
      if (!token || !orgHash) return;

      try {
        setLoadingDropdowns(true);
        const metadata = await userManagementAPI.getMetadata(token, null, orgHash);
        
        setDomains(metadata.dropdowns.domains || []);
        setProperties(metadata.dropdowns.properties || []);
        
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, [props.show, auth.user, orgHash]);

  const handleRadioChange = (event) => {
    setSelectedOption(event.target.id);
    setSelectedProperty("");
  };

  const CustomPopover = ({ show, target, message, title }) => (
    <Overlay show={show} target={target} placement="top" containerPadding={20}>
      <Popover id="popover-message">
        <Popover.Header as="h3">{title}</Popover.Header>
        <Popover.Body>
          {Array.isArray(message) ? (
            <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
              {message.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          ) : (
            message
          )}
        </Popover.Body>
      </Popover>
    </Overlay>
  );

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Comprehensive validation
  const validateForm = () => {
    const errors = [];
    
    // 1. Check if email list is filled
    if (!emailList.trim()) {
      errors.push("Please enter at least one email address");
      return errors;
    }

    // Extract and clean emails
    const emails = emailList
      .split("\n")
      .map(email => email.trim())
      .filter(email => email !== "");

    if (emails.length === 0) {
      errors.push("Please enter at least one valid email address");
      return errors;
    }

    // 2. Validate email format
    const invalidEmails = [];
    emails.forEach((email, index) => {
      if (!isValidEmail(email)) {
        invalidEmails.push(`Line ${index + 1}: "${email}" is not a valid email format`);
      }
    });

    if (invalidEmails.length > 0) {
      errors.push("Invalid email addresses found:");
      errors.push(...invalidEmails);
    }

    // 3. Check if permissions selected
    if (!selectedPermission) {
      errors.push("Please select a permission level");
    }

    // 4. Check if action (radio button) selected
    if (!selectedOption) {
      errors.push("Please select where to add users (All Properties, Domain, or Single Property)");
    }

    // 5. Check if dropdown value selected when needed
    if (selectedOption === "add_all-properties-of-1-domain" && !selectedProperty) {
      errors.push("Please select a domain");
    }

    if (selectedOption === "add_single-properties" && !selectedProperty) {
      errors.push("Please select a property");
    }

    return errors;
  };

  const handleAddUsers = async () => {
    const token = auth.user?.access_token;
    if (!token) {
      showPopover("error", ["Authorization token is missing"]);
      return;
    }

    // Clear previous validation errors
    setValidationErrors([]);

    // Validate form
    const errors = validateForm();
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      showPopover("error", errors);
      return;
    }

    // Extract emails
    const emails = emailList
      .split("\n")
      .map(email => email.trim())
      .filter(email => email !== "");
  
    const action = selectedOption;
    const gsc_property = selectedProperty;
  
    // Format data for API
    const requestData = emails.map(gsc_user => ({
      gsc_user,
      gsc_property,
      action,
      permissions: selectedPermission,
      org: orgHash,
      requestor: auth.user?.profile.sub
    }));
  
    console.log("Request Data:", requestData);
  
    try {
      setIsSubmitting(true);
      const response = await userManagementAPI.addUsers(token, requestData);
      
      console.log("Success! Response:", response);
      
      // Track the user management event in analytics
      const recordsAffected = response.records_created || requestData.length;
      trackUserManagement('add_users', recordsAffected);

      // Clear form values
      setEmailList(""); 
      setSelectedOption(null); 
      setSelectedProperty("");
      setSelectedPermission("");
      setValidationErrors([]);
      
      showPopover("success", "Users added to queue! Check User Management Log for validation status.");
      
      // Close modal after 2 seconds
      setTimeout(() => {
        props.onHide();
      }, 2000);
      
    } catch (error) {
      console.error("Error sending data to API:", error);
      showPopover("error", ["Failed to submit request. Please try again."]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showPopover = (type, message) => {
    setPopoverType(type);
    setTarget(buttonRef.current);
    setShow(true);
    
    setTimeout(() => setShow(false), type === "success" ? 5000 : 15000);
  };

  // Check if this is demo account
  const isDemoAccount = orgHash === DEMO_ACCOUNT_HASH;

  return (
    <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton>
        
        <Modal.Title id="contained-modal-title-vcenter">
        <i className="fas fa-user-plus me-2"></i>&nbsp;Add Users</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Demo account warning banner */}
        {isDemoAccount && !isSuperadmin && (
          <Alert variant="" className="mb-3" style={{backgroundColor: '#fff1cc', borderColor: '#ffecb8', color: 'black'}}>
            <i className="fas fa-info-circle me-2"></i>&nbsp;
            <strong>Functional Preview Account</strong>
            <br />
            This is a functional preview account. Any email you add will be added to the backlog, but will be removed before processing with backlog jobs.
          </Alert>
        )}
        
        {isSubmitting ? (
          <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner message="Submitting user requests..." />
          </div>
        ) : (
          <Form>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group >
                  <Form.Label className='addUsers'>
                    List email addresses (one per line)
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    id="add-users-modal"
                    rows="10"
                    value={emailList}
                    onChange={(e) => setEmailList(e.target.value)}
                    isInvalid={validationErrors.length > 0 && !emailList.trim()}
                    disabled={loadingDropdowns}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                {/* Permissions Dropdown */}
                <Form.Group className="mb-3">
                  <Form.Label>Permissions</Form.Label>
                  <DataDropdown 
                    setSelectedProperty={setSelectedPermission} 
                    data={permissions} 
                    placeholder="Select Permission"
                    inputID="permissions"
                    inputName="permissionsPropertiesModal" 
                    zIndex="1001"
                    value={selectedPermission}
                  />
                </Form.Group>

                {/* Radio buttons for action selection */}
                <Form.Group id="properties" style={{ marginTop: "1rem" }}>
                  <Form.Check
                    label="All GSC Properties"
                    type="radio"
                    name="add-where-to-add"
                    id="add_all-gsc-properties"
                    checked={selectedOption === "add_all-gsc-properties"}
                    onChange={handleRadioChange}
                    disabled={loadingDropdowns}
                  />
                  <Form.Check
                    label="All sub-properties of one domain"
                    type="radio"
                    name="add-where-to-add"
                    id="add_all-properties-of-1-domain"
                    checked={selectedOption === "add_all-properties-of-1-domain"}
                    onChange={handleRadioChange}
                    disabled={loadingDropdowns}
                  />
                  <Form.Check
                    label="Single properties"
                    type="radio"
                    name="add-where-to-add"
                    id="add_single-properties"
                    checked={selectedOption === "add_single-properties"}
                    onChange={handleRadioChange}
                    disabled={loadingDropdowns}
                  />
                  
                  {/* Conditional dropdowns container */}
                  <div id="properties-dropdown" style={{ minHeight: '50px', marginTop: '10px' }}>
                    {selectedOption === "add_all-properties-of-1-domain" && (
                      <DataDropdown 
                        setSelectedProperty={setSelectedProperty} 
                        data={domains} 
                        placeholder="Select Domain"
                        inputID="gscPropSelector"
                        inputName="domainPropertiesModal" 
                        zIndex="1000"
                        value={selectedProperty}
                        key={`domain-${selectedProperty}`}
                      />
                    )}
                    
                    {selectedOption === "add_single-properties" && (
                      <DataDropdown 
                        setSelectedProperty={setSelectedProperty} 
                        data={properties} 
                        placeholder="Select Property"
                        inputID="gscPropSelector" 
                        inputName="gscPropPropertiesModal" 
                        zIndex="1000"
                        value={selectedProperty}
                        key={`property-${selectedProperty}`}
                      />
                    )}
                  </div>
                </Form.Group>

                {/* Add Users Button */}
                <div style={{ marginTop: '25px' }}>
                  <Button 
                    ref={buttonRef} 
                    className="rounded w-100" 
                    onClick={handleAddUsers}
                    disabled={loadingDropdowns}
                    style={{
                      backgroundColor: '#1abc9c',
                      borderColor: '#1abc9c'
                    }}
                  >
                    Add Users
                  </Button>
                  <CustomPopover
                    show={show}
                    target={target}
                    title={popoverType === "success" ? "Success!" : "Validation Errors"}
                    message={
                      popoverType === "success"
                        ? "Users added to queue! Check User Management Log for validation status."
                        : validationErrors
                    }
                  />
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default AddUsersModal;

















// import React, { useState, useRef, useEffect } from 'react';
// import Form from 'react-bootstrap/Form';
// import { Popover, Overlay, Row, Col, Button, Modal } from 'react-bootstrap';
// import './Modal.css';
// import './Dropdowns.css';
// import DataDropdown from './DataDropdown';
// import LoadingSpinner from './LoadingSpinner';
// import { useAuth } from "react-oidc-context";
// import { useUser } from "../context/UserContext";
// import { userManagementAPI } from '../utils/apiClient';

// function AddUsersModal(props) {
//   const auth = useAuth();
//   const { orgHash } = useUser();
  
//   // Form state
//   const [emailList, setEmailList] = useState("");
//   const [selectedOption, setSelectedOption] = useState(null);
//   const [selectedProperty, setSelectedProperty] = useState("");
//   const [selectedPermission, setSelectedPermission] = useState("");
  
//   // Dropdown data from API
//   const [domains, setDomains] = useState([]);
//   const [properties, setProperties] = useState([]);
//   const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
//   // Submission loading state
//   const [isSubmitting, setIsSubmitting] = useState(false);
  
//   // Validation state
//   const [validationErrors, setValidationErrors] = useState([]);
  
//   // Static permissions list
//   const permissions = ["Restricted", "Full", "Owner"];
  
//   // Popover state
//   const [show, setShow] = useState(false);
//   const [target, setTarget] = useState(null);
//   const [popoverType, setPopoverType] = useState("");
//   const buttonRef = useRef(null);

//   // Fetch dropdown data when modal opens
//   useEffect(() => {
//     const fetchDropdownData = async () => {
//       if (!props.show) return;
      
//       const token = auth.user?.access_token;
//       if (!token || !orgHash) return;

//       try {
//         setLoadingDropdowns(true);
//         const metadata = await userManagementAPI.getMetadata(token, null, orgHash);
        
//         setDomains(metadata.dropdowns.domains || []);
//         setProperties(metadata.dropdowns.properties || []);
        
//       } catch (err) {
//         console.error('Error fetching dropdown data:', err);
//       } finally {
//         setLoadingDropdowns(false);
//       }
//     };

//     fetchDropdownData();
//   }, [props.show, auth.user, orgHash]);

//   const handleRadioChange = (event) => {
//     setSelectedOption(event.target.id);
//     setSelectedProperty("");
//   };

//   const CustomPopover = ({ show, target, message, title }) => (
//     <Overlay show={show} target={target} placement="top" containerPadding={20}>
//       <Popover id="popover-message">
//         <Popover.Header as="h3">{title}</Popover.Header>
//         <Popover.Body>
//           {Array.isArray(message) ? (
//             <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
//               {message.map((msg, idx) => (
//                 <li key={idx}>{msg}</li>
//               ))}
//             </ul>
//           ) : (
//             message
//           )}
//         </Popover.Body>
//       </Popover>
//     </Overlay>
//   );

//   // Email validation regex
//   const isValidEmail = (email) => {
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return emailRegex.test(email);
//   };

//   // Comprehensive validation
//   const validateForm = () => {
//     const errors = [];
    
//     // 1. Check if email list is filled
//     if (!emailList.trim()) {
//       errors.push("Please enter at least one email address");
//       return errors;
//     }

//     // Extract and clean emails
//     const emails = emailList
//       .split("\n")
//       .map(email => email.trim())
//       .filter(email => email !== "");

//     if (emails.length === 0) {
//       errors.push("Please enter at least one valid email address");
//       return errors;
//     }

//     // 2. Validate email format
//     const invalidEmails = [];
//     emails.forEach((email, index) => {
//       if (!isValidEmail(email)) {
//         invalidEmails.push(`Line ${index + 1}: "${email}" is not a valid email format`);
//       }
//     });

//     if (invalidEmails.length > 0) {
//       errors.push("Invalid email addresses found:");
//       errors.push(...invalidEmails);
//     }

//     // 3. Check if permissions selected
//     if (!selectedPermission) {
//       errors.push("Please select a permission level");
//     }

//     // 4. Check if action (radio button) selected
//     if (!selectedOption) {
//       errors.push("Please select where to add users (All Properties, Domain, or Single Property)");
//     }

//     // 5. Check if dropdown value selected when needed
//     if (selectedOption === "add_all-properties-of-1-domain" && !selectedProperty) {
//       errors.push("Please select a domain");
//     }

//     if (selectedOption === "add_single-properties" && !selectedProperty) {
//       errors.push("Please select a property");
//     }

//     return errors;
//   };

//   const handleAddUsers = async () => {
//     const token = auth.user?.access_token;
//     if (!token) {
//       showPopover("error", ["Authorization token is missing"]);
//       return;
//     }

//     // Clear previous validation errors
//     setValidationErrors([]);

//     // Validate form
//     const errors = validateForm();
    
//     if (errors.length > 0) {
//       setValidationErrors(errors);
//       showPopover("error", errors);
//       return;
//     }

//     // Extract emails
//     const emails = emailList
//       .split("\n")
//       .map(email => email.trim())
//       .filter(email => email !== "");
  
//     const action = selectedOption;
//     const gsc_property = selectedProperty;
  
//     // Format data for API
//     const requestData = emails.map(gsc_user => ({
//       gsc_user,
//       gsc_property,
//       action,
//       permissions: selectedPermission,
//       org: orgHash,
//       requestor: auth.user?.profile.sub
//     }));
  
//     console.log("Request Data:", requestData);
  
//     try {
//       setIsSubmitting(true);
//       const response = await userManagementAPI.addUsers(token, requestData);
      
//       console.log("Success! Response:", response);
      
//       // Clear form values
//       setEmailList(""); 
//       setSelectedOption(null); 
//       setSelectedProperty("");
//       setSelectedPermission("");
//       setValidationErrors([]);
      
//       showPopover("success", "Users added to queue! Check User Management Log for validation status.");
      
//       // Close modal after 2 seconds
//       setTimeout(() => {
//         props.onHide();
//       }, 2000);
      
//     } catch (error) {
//       console.error("Error sending data to API:", error);
//       showPopover("error", ["Failed to submit request. Please try again."]);
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const showPopover = (type, message) => {
//     setPopoverType(type);
//     setTarget(buttonRef.current);
//     setShow(true);
    
//     setTimeout(() => setShow(false), type === "success" ? 5000 : 15000);
//   };

//   return (
//     <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
//       <Modal.Header>
//         <Button 
//           variant="link" 
//           onClick={props.onHide} 
//           style={{ 
//             position: "absolute", 
//             right: "10px", 
//             top: "10px", 
//             fontWeight: "500", 
//             fontSize: "medium" 
//           }}
//           disabled={isSubmitting}
//         >
//           x
//         </Button>
//         <Modal.Title id="contained-modal-title-vcenter">Add Users</Modal.Title>
//       </Modal.Header>
//       <Modal.Body>
//         {isSubmitting ? (
//           <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//             <LoadingSpinner message="Submitting user requests..." />
//           </div>
//         ) : (
//           <Form>
//             <Row>
//               <Col xs={12} md={6}>
//                 <Form.Group>
//                   <Form.Label className='addUsers'>
//                     List email addresses (one per line)
//                   </Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows="10"
//                     value={emailList}
//                     onChange={(e) => setEmailList(e.target.value)}
//                     isInvalid={validationErrors.length > 0 && !emailList.trim()}
//                     disabled={loadingDropdowns}
//                   />
//                 </Form.Group>
//               </Col>
//               <Col xs={12} md={6}>
//                 {/* Permissions Dropdown */}
//                 <Form.Group className="mb-3">
//                   <Form.Label>Permissions</Form.Label>
//                   <DataDropdown 
//                     setSelectedProperty={setSelectedPermission} 
//                     data={permissions} 
//                     placeholder="Select Permission"
//                     inputID="gscPropSelector"
//                     inputName="permissionsPropertiesModal" 
//                     zIndex="1001"
//                     value={selectedPermission}
//                   />
//                 </Form.Group>

//                 {/* Radio buttons for action selection */}
//                 <Form.Group style={{ marginTop: "1rem" }}>
//                   <Form.Check
//                     label="All GSC Properties"
//                     type="radio"
//                     name="add-where-to-add"
//                     id="add_all-gsc-properties"
//                     checked={selectedOption === "add_all-gsc-properties"}
//                     onChange={handleRadioChange}
//                     disabled={loadingDropdowns}
//                   />
//                   <Form.Check
//                     label="All sub-properties of one domain"
//                     type="radio"
//                     name="add-where-to-add"
//                     id="add_all-properties-of-1-domain"
//                     checked={selectedOption === "add_all-properties-of-1-domain"}
//                     onChange={handleRadioChange}
//                     disabled={loadingDropdowns}
//                   />
//                   <Form.Check
//                     label="Single properties"
//                     type="radio"
//                     name="add-where-to-add"
//                     id="add_single-properties"
//                     checked={selectedOption === "add_single-properties"}
//                     onChange={handleRadioChange}
//                     disabled={loadingDropdowns}
//                   />
                  
//                   {/* Conditional dropdowns container */}
//                   <div style={{ minHeight: '50px', marginTop: '10px' }}>
//                     {selectedOption === "add_all-properties-of-1-domain" && (
//                       <DataDropdown 
//                         setSelectedProperty={setSelectedProperty} 
//                         data={domains} 
//                         placeholder="Select Domain"
//                         inputID="gscPropSelector"
//                         inputName="domainPropertiesModal" 
//                         zIndex="1000"
//                         value={selectedProperty}
//                         key={`domain-${selectedProperty}`}
//                       />
//                     )}
                    
//                     {selectedOption === "add_single-properties" && (
//                       <DataDropdown 
//                         setSelectedProperty={setSelectedProperty} 
//                         data={properties} 
//                         placeholder="Select Property"
//                         inputID="gscPropSelector" 
//                         inputName="gscPropPropertiesModal" 
//                         zIndex="1000"
//                         value={selectedProperty}
//                         key={`property-${selectedProperty}`}
//                       />
//                     )}
//                   </div>
//                 </Form.Group>

//                 {/* Add Users Button */}
//                 <div style={{ marginTop: '25px' }}>
//                   <Button 
//                     ref={buttonRef} 
//                     className="rounded w-100" 
//                     onClick={handleAddUsers}
//                     disabled={loadingDropdowns}
//                     style={{
//                       backgroundColor: '#1abc9c',
//                       borderColor: '#1abc9c'
//                     }}
//                   >
//                     Add Users
//                   </Button>
//                   <CustomPopover
//                     show={show}
//                     target={target}
//                     title={popoverType === "success" ? "Success!" : "Validation Errors"}
//                     message={
//                       popoverType === "success"
//                         ? "Users added to queue! Check User Management Log for validation status."
//                         : validationErrors
//                     }
//                   />
//                 </div>
//               </Col>
//             </Row>
//           </Form>
//         )}
//       </Modal.Body>
//     </Modal>
//   );
// }

// export default AddUsersModal;


































// import React, { useState, useRef, useEffect } from 'react';
// import Form from 'react-bootstrap/Form';
// import { Popover, Overlay, Row, Col, Button, Modal, Spinner } from 'react-bootstrap';
// import './Modal.css';
// import './Dropdowns.css';
// import DataDropdown from './DataDropdown';
// import { useAuth } from "react-oidc-context";
// import { useUser } from "../context/UserContext";
// import { userManagementAPI } from '../utils/apiClient';

// function AddUsersModal(props) {
//   const auth = useAuth();
//   const { orgHash } = useUser();
  
//   // Form state
//   const [emailList, setEmailList] = useState("");
//   const [selectedOption, setSelectedOption] = useState(null);
//   const [selectedProperty, setSelectedProperty] = useState("");
//   const [selectedPermission, setSelectedPermission] = useState("");
  
//   // Dropdown data from API
//   const [domains, setDomains] = useState([]);
//   const [properties, setProperties] = useState([]);
//   const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
//   // Validation state
//   const [validationErrors, setValidationErrors] = useState([]);
  
//   // Static permissions list
//   const permissions = ["Restricted", "Full", "Owner"];
  
//   // Popover state
//   const [show, setShow] = useState(false);
//   const [target, setTarget] = useState(null);
//   const [popoverType, setPopoverType] = useState("");
//   const buttonRef = useRef(null);

//   // Fetch dropdown data when modal opens
//   useEffect(() => {
//     const fetchDropdownData = async () => {
//       if (!props.show) return;
      
//       const token = auth.user?.access_token;
//       if (!token || !orgHash) return;

//       try {
//         setLoadingDropdowns(true);
//         const metadata = await userManagementAPI.getMetadata(token, null, orgHash);
        
//         setDomains(metadata.dropdowns.domains || []);
//         setProperties(metadata.dropdowns.properties || []);
        
//       } catch (err) {
//         console.error('Error fetching dropdown data:', err);
//       } finally {
//         setLoadingDropdowns(false);
//       }
//     };

//     fetchDropdownData();
//   }, [props.show, auth.user, orgHash]);

//   const handleRadioChange = (event) => {
//     setSelectedOption(event.target.id);
//     setSelectedProperty("");
//   };

//   const CustomPopover = ({ show, target, message, title }) => (
//     <Overlay show={show} target={target} placement="top" containerPadding={20}>
//       <Popover id="popover-message">
//         <Popover.Header as="h3">{title}</Popover.Header>
//         <Popover.Body>
//           {Array.isArray(message) ? (
//             <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
//               {message.map((msg, idx) => (
//                 <li key={idx}>{msg}</li>
//               ))}
//             </ul>
//           ) : (
//             message
//           )}
//         </Popover.Body>
//       </Popover>
//     </Overlay>
//   );

//   // Email validation regex
//   const isValidEmail = (email) => {
//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return emailRegex.test(email);
//   };

//   // Comprehensive validation (without Google account check)
//   const validateForm = () => {
//     const errors = [];
    
//     // 1. Check if email list is filled
//     if (!emailList.trim()) {
//       errors.push("Please enter at least one email address");
//       return errors;
//     }

//     // Extract and clean emails
//     const emails = emailList
//       .split("\n")
//       .map(email => email.trim())
//       .filter(email => email !== "");

//     if (emails.length === 0) {
//       errors.push("Please enter at least one valid email address");
//       return errors;
//     }

//     // 2. Validate email format
//     const invalidEmails = [];
//     emails.forEach((email, index) => {
//       if (!isValidEmail(email)) {
//         invalidEmails.push(`Line ${index + 1}: "${email}" is not a valid email format`);
//       }
//     });

//     if (invalidEmails.length > 0) {
//       errors.push("Invalid email addresses found:");
//       errors.push(...invalidEmails);
//     }

//     // 3. Check if permissions selected
//     if (!selectedPermission) {
//       errors.push("Please select a permission level");
//     }

//     // 4. Check if action (radio button) selected
//     if (!selectedOption) {
//       errors.push("Please select where to add users (All Properties, Domain, or Single Property)");
//     }

//     // 5. Check if dropdown value selected when needed
//     if (selectedOption === "add_all-properties-of-1-domain" && !selectedProperty) {
//       errors.push("Please select a domain");
//     }

//     if (selectedOption === "add_single-properties" && !selectedProperty) {
//       errors.push("Please select a property");
//     }

//     return errors;
//   };

//   const handleAddUsers = async () => {
//     const token = auth.user?.access_token;
//     if (!token) {
//       showPopover("error", ["Authorization token is missing"]);
//       return;
//     }

//     // Clear previous validation errors
//     setValidationErrors([]);

//     // Validate form
//     const errors = validateForm();
    
//     if (errors.length > 0) {
//       setValidationErrors(errors);
//       showPopover("error", errors);
//       return;
//     }

//     // Extract emails
//     const emails = emailList
//       .split("\n")
//       .map(email => email.trim())
//       .filter(email => email !== "");
  
//     const action = selectedOption;
//     const gsc_property = selectedProperty;
  
//     // Format data for API
//     const requestData = emails.map(gsc_user => ({
//       gsc_user,
//       gsc_property,
//       action,
//       permissions: selectedPermission,
//       org: orgHash,
//       requestor: auth.user?.profile.sub
//     }));
  
//     console.log("Request Data:", requestData);
  
//     try {
//       const response = await userManagementAPI.addUsers(token, requestData);
      
//       console.log("Success! Response:", response);
      
//       // Clear form values
//       setEmailList(""); 
//       setSelectedOption(null); 
//       setSelectedProperty("");
//       setSelectedPermission("");
//       setValidationErrors([]);
      
//       showPopover("success", "Users added to queue! Check User Management Log for validation status.");
      
//       // Close modal after 2 seconds
//       setTimeout(() => {
//         props.onHide();
//       }, 2000);
      
//     } catch (error) {
//       console.error("Error sending data to API:", error);
//       showPopover("error", ["Failed to submit request. Please try again."]);
//     }
//   };

//   const showPopover = (type, message) => {
//     setPopoverType(type);
//     setTarget(buttonRef.current);
//     setShow(true);
    
//     setTimeout(() => setShow(false), type === "success" ? 5000 : 15000);
//   };

//   return (
//     <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
//       <Modal.Header>
//         <Button 
//           variant="link" 
//           onClick={props.onHide} 
//           style={{ 
//             position: "absolute", 
//             right: "10px", 
//             top: "10px", 
//             fontWeight: "500", 
//             fontSize: "medium" 
//           }}
//         >
//           x
//         </Button>
//         <Modal.Title id="contained-modal-title-vcenter">Add Users</Modal.Title>
//       </Modal.Header>
//       <Modal.Body>
//         {loadingDropdowns && (
//           <div className="text-center mb-3">
//             <Spinner animation="border" size="sm" /> Loading dropdown data...
//           </div>
//         )}
        
//         <Form>
//           <Row>
//             <Col xs={12} md={6}>
//               <Form.Group>
//                 <Form.Label className='addUsers'>
//                   List email addresses (one per line)
//                 </Form.Label>
//                 <Form.Control
//                   as="textarea"
//                   rows="10"
//                   value={emailList}
//                   onChange={(e) => setEmailList(e.target.value)}
//                   isInvalid={validationErrors.length > 0 && !emailList.trim()}
//                 />
//               </Form.Group>
//             </Col>
//             <Col xs={12} md={6}>
//               {/* Permissions Dropdown - using gscPropSelector ID for consistent 300px width */}
//               <Form.Group className="mb-3">
//                 <Form.Label>Permissions</Form.Label>
//                 <DataDropdown 
//                   setSelectedProperty={setSelectedPermission} 
//                   data={permissions} 
//                   placeholder="Select Permission"
//                   inputID="gscPropSelector"
//                   inputName="permissionsPropertiesModal" 
//                   zIndex="1001"
//                   value={selectedPermission}
//                 />
//               </Form.Group>

//               {/* Radio buttons for action selection */}
//               <Form.Group style={{ marginTop: "1rem" }}>
//                 <Form.Check
//                   label="All GSC Properties"
//                   type="radio"
//                   name="add-where-to-add"
//                   id="add_all-gsc-properties"
//                   checked={selectedOption === "add_all-gsc-properties"}
//                   onChange={handleRadioChange}
//                 />
//                 <Form.Check
//                   label="All sub-properties of one domain"
//                   type="radio"
//                   name="add-where-to-add"
//                   id="add_all-properties-of-1-domain"
//                   checked={selectedOption === "add_all-properties-of-1-domain"}
//                   onChange={handleRadioChange}
//                 />
//                 <Form.Check
//                   label="Single properties"
//                   type="radio"
//                   name="add-where-to-add"
//                   id="add_single-properties"
//                   checked={selectedOption === "add_single-properties"}
//                   onChange={handleRadioChange}
//                 />
                
//                 {/* Conditional dropdowns container - fixed height */}
//                 <div style={{ minHeight: '50px', marginTop: '10px' }}>
//                   {/* Show domain dropdown - uses gscPropSelector for 300px width */}
//                   {selectedOption === "add_all-properties-of-1-domain" && (
//                     <DataDropdown 
//                       setSelectedProperty={setSelectedProperty} 
//                       data={domains} 
//                       placeholder="Select Domain"
//                       inputID="gscPropSelector"
//                       inputName="domainPropertiesModal" 
//                       zIndex="1000"
//                       value={selectedProperty}
//                       key={`domain-${selectedProperty}`}
//                     />
//                   )}
                  
//                   {/* Show property dropdown - uses gscPropSelector for 300px width */}
//                   {selectedOption === "add_single-properties" && (
//                     <DataDropdown 
//                       setSelectedProperty={setSelectedProperty} 
//                       data={properties} 
//                       placeholder="Select Property"
//                       inputID="gscPropSelector" 
//                       inputName="gscPropPropertiesModal" 
//                       zIndex="1000"
//                       value={selectedProperty}
//                       key={`property-${selectedProperty}`}
//                     />
//                   )}
//                 </div>
//               </Form.Group>

//               {/* Add Users Button - fixed position */}
//               <div style={{ marginTop: '25px' }}>
//                 <Button 
//                   ref={buttonRef} 
//                   className="rounded w-100" 
//                   onClick={handleAddUsers}
//                   disabled={loadingDropdowns}
//                   style={{
//                     backgroundColor: '#1abc9c',
//                     borderColor: '#1abc9c'
//                   }}
//                 >
//                   Add Users
//                 </Button>
//                 <CustomPopover
//                   show={show}
//                   target={target}
//                   title={popoverType === "success" ? "Success!" : "Validation Errors"}
//                   message={
//                     popoverType === "success"
//                       ? "Users added to queue! Check User Management Log for validation status."
//                       : validationErrors
//                   }
//                 />
//               </div>
//             </Col>
//           </Row>
//         </Form>
//       </Modal.Body>
//     </Modal>
//   );
// }

// export default AddUsersModal;